import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { rankCandidates, type MatchCandidate, type TxCandidate } from '@/lib/reconciliation';
import { useUpcomingPayables, useUpcomingReceivables } from '@/hooks/useFinancialData';

export interface PendingTx {
  id: string;
  description: string;
  direction: 'in' | 'out';
  amount_cents: number;
  occurred_at: string;
  account_name: string;
  reconciled: boolean;
}

export function useReconciliation() {
  const { tenantId } = useTenant();
  const { data: payables } = useUpcomingPayables();
  const { data: receivables } = useUpcomingReceivables();

  const [pendingTxs, setPendingTxs]   = useState<PendingTx[]>([]);
  const [allTxs, setAllTxs]           = useState<PendingTx[]>([]);
  const [loading, setLoading]         = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const fetch = useCallback(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    supabase
      .from('transactions')
      .select('id, description, direction, amount_cents, occurred_at, reconciled, account:accounts(name)')
      .eq('tenant_id', tenantId)
      .order('occurred_at', { ascending: false })
      .limit(200)
      .then(({ data, error: fetchErr }) => {
        if (fetchErr) { setLoading(false); return; }
        const rows = (data ?? []) as unknown as Array<{
          id: string; description: string; direction: string;
          amount_cents: number; occurred_at: string; reconciled: boolean;
          account: { name: string } | null;
        }>;
        const mapped: PendingTx[] = rows.map((r) => ({
          id: r.id,
          description: r.description,
          direction: r.direction as 'in' | 'out',
          amount_cents: r.amount_cents,
          occurred_at: r.occurred_at,
          account_name: r.account?.name ?? '—',
          reconciled: r.reconciled,
        }));
        setAllTxs(mapped);
        setPendingTxs(mapped.filter((t) => !t.reconciled));
        setLoading(false);
      });
  }, [tenantId]);

  useEffect(() => { fetch(); }, [fetch]);

  function suggestMatches(tx: PendingTx): MatchCandidate[] {
    const txC: TxCandidate = {
      id: tx.id,
      direction: tx.direction,
      amount_cents: tx.amount_cents,
      description: tx.description,
      occurred_at: tx.occurred_at,
    };
    const pC = payables.map((p) => ({
      id: p.id, payee: p.payee, description: p.description,
      amount_cents: p.amount_cents, due: p.due, status: p.status as 'pending' | 'overdue',
    }));
    const rC = receivables.map((r) => ({
      id: r.id, payee: r.payee, description: r.description,
      amount_cents: r.amount_cents, due: r.due, status: r.status as 'pending' | 'overdue',
    }));
    return rankCandidates(txC, pC, rC);
  }

  async function reconcile(txId: string, targetId: string, targetKind: 'payable' | 'receivable') {
    setReconciling(true);
    const { error } = await supabase.rpc('reconcile_transaction' as never, {
      p_tx_id: txId,
      p_target_id: targetId,
      p_target_kind: targetKind,
    } as never);
    setReconciling(false);
    if (error) throw new Error(error.message);
    fetch();
  }

  async function unreconcile(txId: string) {
    setReconciling(true);
    const { error } = await supabase.rpc('unreconcile_transaction' as never, { p_tx_id: txId } as never);
    setReconciling(false);
    if (error) throw new Error(error.message);
    fetch();
  }

  async function bulkReconcile(pairs: Array<{ tx_id: string; target_id: string; target_kind: 'payable' | 'receivable' }>) {
    let success = 0;
    const errors: string[] = [];
    for (const p of pairs) {
      try {
        await reconcile(p.tx_id, p.target_id, p.target_kind);
        success++;
      } catch (e: any) {
        errors.push(e.message);
      }
    }
    return { success, errors };
  }

  return {
    pendingTxs, allTxs, loading, reconciling,
    suggestMatches, reconcile, unreconcile, bulkReconcile,
    refetch: fetch,
  };
}
